import * as Cesium from 'cesium'
import { e as w } from '../../utils/gcoord'

/**
 * 高德地图影像提供者类
 * 继承自Cesium.UrlTemplateImageryProvider，提供高德地图各种图层的访问
 */
export class AmapImageryProvider extends Cesium.UrlTemplateImageryProvider {
  /**
   * 构造函数
   * @param {Object} options - 配置选项
   * @param {string} options.type - 地图类型
   * @param {string} [options.crs] - 坐标系类型，支持'WGS84'或其他
   * @param {Array<string>} [options.subdomains] - 子域名数组
   * @param {number} [options.minimumLevel] - 最小层级
   * @param {number} [options.maximumLevel] - 最大层级
   */
  constructor (options = {}) {
    // 高德地图服务URL模板配置
    const urlTemplates = {
      // 影像底图（无标注）
      img_w:
        'https://webst0{s}.is.autonavi.com/appmaptile?lang=zh_cn&size=1&scale=1&style=6&x={x}&y={y}&z={z}',

      // 矢量底图（无标注）
      vec_w:
        'https://wprd0{s}.is.autonavi.com/appmaptile?lang=zh_cn&size=1&scale=1&style=7&ltype=3&x={x}&y={y}&z={z}',

      // 道路图
      road_w:
        'https://webst0{s}.is.autonavi.com/appmaptile?lang=zh_cn&size=1&scale=1&style=8&ltype=11&x={x}&y={y}&z={z}',

      // 矢量底图+标注
      vec_label_w:
        'https://webrd0{s}.is.autonavi.com/appmaptile?lang=zh_cn&size=1&scale=1&style=7&x={x}&y={y}&z={z}',

      // 道路图+标注
      road_label_w:
        'https://webst0{s}.is.autonavi.com/appmaptile?lang=zh_cn&size=1&scale=1&style=8&x={x}&y={y}&z={z}',

      // 纯标注图层
      label_w:
        'https://webst02.is.autonavi.com/appmaptile?style=8&x={x}&y={y}&z={z}',

      // 新版影像底图
      new_img_url:
        'https://wprd0{s}.is.autonavi.com/appmaptile?x={x}&y={y}&z={z}&lang=zh_cn&size=1&scl=1&style=6',

      // 新版标注图层
      new_label_w:
        'https://wprd0{s}.is.autonavi.com/appmaptile?x={x}&y={y}&z={z}&lang=zh_cn&size=1&scl=1&style=8',
    }

    // 根据类型设置URL
    const config = { ...options }
    if (urlTemplates[config.type]) {
      config.url = urlTemplates[config.type]
    }

    // 设置默认子域名
    if (!config.subdomains || !config.subdomains.length) {
      config.subdomains = ['1', '2', '3', '4']
    }

    // 如果使用WGS84坐标系，应用自定义的瓦片方案来处理坐标偏移
    if (config.crs === 'WGS84') {
      config.tilingScheme = new GCJ02WebMercatorTilingScheme()
    }

    // 设置默认的最小和最大层级
    if (config.minimumLevel === undefined) {
      config.minimumLevel = 1
    }
    if (config.maximumLevel === undefined) {
      config.maximumLevel = 18
    }

    // 调用父类构造函数
    super(config)

    // 设置内部属性
    this._maximumLevel = config.maximumLevel
    this._type = config.type
    this._crs = config.crs
  }

  /**
   * 获取地图类型
   * @returns {string} 地图类型
   */
  getType () {
    return this._type
  }

  /**
   * 获取坐标系类型
   * @returns {string} 坐标系类型
   */
  getCRS () {
    return this._crs
  }

  /**
   * 获取最大层级
   * @returns {number} 最大层级
   */
  getMaximumLevel () {
    return this._maximumLevel
  }

  /**
   * 设置最大层级
   * @param {number} level - 最大层级
   */
  setMaximumLevel (level) {
    this._maximumLevel = level
  }
}

/**
 * 高德地图瓦片方案类（处理GCJ02坐标偏移）
 * 继承自Cesium.WebMercatorTilingScheme，处理WGS84到GCJ02的坐标转换
 */
export class GCJ02WebMercatorTilingScheme
  extends Cesium.WebMercatorTilingScheme {
  /**
   * 构造函数
   * @param {Object} options - 配置选项
   */
  constructor (options = {}) {
    super(options)

    const webMercatorProjection = new Cesium.WebMercatorProjection()

    // 设置瓦片尺寸
    this._tileWidth = 256
    this._tileHeight = 256
    this._maximumLevel = 19

    /**
     * 重写投影方法：WGS84 → GCJ02 → Web墨卡托
     * @param {Cesium.Cartographic} cartographic - 地理坐标
     * @param {Cesium.Cartesian2} result - 结果坐标
     * @returns {Cesium.Cartesian2} 投影后的坐标
     */
    this._projection.project = function (cartographic, result) {
      // 将弧度转换为度
      const longitudeDeg = Cesium.Math.toDegrees(cartographic.longitude)
      const latitudeDeg = Cesium.Math.toDegrees(cartographic.latitude)

      // WGS84坐标转换为GCJ02坐标（火星坐标系）
      const gcj02Coord = w.transform(
        [longitudeDeg, latitudeDeg],
        w.WGS84,
        w.GCJ02,
      )

      // 将GCJ02坐标转换为Web墨卡托投影
      const projectedCoord = webMercatorProjection.project(
        new Cesium.Cartographic(
          Cesium.Math.toRadians(gcj02Coord[0]),
          Cesium.Math.toRadians(gcj02Coord[1]),
        ),
      )

      return new Cesium.Cartesian2(projectedCoord.x, projectedCoord.y)
    }

    /**
     * 重写反投影方法：Web墨卡托 → GCJ02 → WGS84
     * @param {Cesium.Cartesian2} cartesian - 投影坐标
     * @param {Cesium.Cartographic} result - 结果地理坐标
     * @returns {Cesium.Cartographic} 反投影后的地理坐标
     */
    this._projection.unproject = function (cartesian, result) {
      // Web墨卡托投影转换为地理坐标
      const cartographic = webMercatorProjection.unproject(cartesian)

      // 将弧度转换为度
      const longitudeDeg = Cesium.Math.toDegrees(cartographic.longitude)
      const latitudeDeg = Cesium.Math.toDegrees(cartographic.latitude)

      // GCJ02坐标转换回WGS84坐标
      const wgs84Coord = w.transform(
        [longitudeDeg, latitudeDeg],
        w.GCJ02,
        w.WGS84,
      )

      return new Cesium.Cartographic(
        Cesium.Math.toRadians(wgs84Coord[0]),
        Cesium.Math.toRadians(wgs84Coord[1]),
      )
    }
  }
}

/**
 * 百度地图影像提供者类
 * 继承自Cesium.UrlTemplateImageryProvider，提供百度地图各种图层的访问
 */
export class BaiduImageryProvider extends Cesium.UrlTemplateImageryProvider {
  /**
   * 构造函数
   * @param {Object} options - 配置选项
   * @param {string} options.type - 地图类型
   * @param {string} [options.crs] - 坐标系类型，支持'WGS84'或其他
   * @param {Array<string>} [options.subdomains] - 子域名数组
   * @param {number} [options.minimumLevel] - 最小层级
   * @param {number} [options.maximumLevel] - 最大层级
   */
  constructor (options = {}) {
    // 百度地图服务URL模板配置
    const urlTemplates = {
      img: 'https://maponline{s}.bdimg.com/starpic/?qt=satepc&u=x={x};y={y};z={z};v=009;type=sate&fm=46&app=webearth2&v=009&udt=20260219',
      img_w: 'https://maponline{s}.bdimg.com/starpic/?qt=satepc&u=x={x};y={y};z={z};v=009;type=sate&fm=46&app=webearth2&v=009&udt=20260219',
      img_label: 'https://maponline{s}.bdimg.com/tile/?qt=vtile&x={x}&y={y}&z={z}&styles=sl&showtext=0&v=083&udt=20260219',
      // 可根据需要补充其他类型
    }

    const type = options.type || 'img'
    const url = urlTemplates[type] || urlTemplates.img

    // 自定义标签，处理百度瓦片坐标偏移
    const customTags = {
      x: (imageryProvider, x, y, level) => x - (1 << (level - 1)),
      y: (imageryProvider, x, y, level) => (1 << (level - 1)) - y - 1,
    }

    const config = {
      url: url,
      subdomains: options.subdomains || ['0', '1', '2', '3'],
      minimumLevel: options.minimumLevel || 1,
      maximumLevel: options.maximumLevel || 18,
      tilingScheme: options.crs === 'WGS84' ? new BD09WebMercatorTilingScheme() : undefined,
      customTags: customTags,
      enablePickFeatures: false,
    }

    super(config)
    this._type = type
    this._crs = options.crs
  }

  /**
   * 获取地图类型
   * @returns {string} 地图类型
   */
  getType () {
    return this._type
  }

  /**
   * 获取坐标系类型
   * @returns {string} 坐标系类型
   */
  getCRS () {
    return this._crs
  }

  /**
   * 获取最大层级
   * @returns {number} 最大层级
   */
  getMaximumLevel () {
    return this._maximumLevel
  }

  /**
   * 设置最大层级
   * @param {number} level - 最大层级
   */
  setMaximumLevel (level) {
    this._maximumLevel = level
  }
}

/**
 * 百度地图瓦片方案类（处理BD09坐标偏移）
 * 继承自Cesium.WebMercatorTilingScheme，处理WGS84到BD09的坐标转换
 */
export class BD09WebMercatorTilingScheme
  extends Cesium.WebMercatorTilingScheme {
  /**
   * 构造函数
   * @param {Object} options - 配置选项
   */
  constructor (options = {}) {
    super(options)

    const webMercatorProjection = new Cesium.WebMercatorProjection()

    this._tileWidth = 256
    this._tileHeight = 256
    this._maximumLevel = 19

    /**
     * 重写投影方法：WGS84 → BD09 → Web墨卡托
     */
    this._projection.project = function (cartographic, result) {
      const longitudeDeg = Cesium.Math.toDegrees(cartographic.longitude)
      const latitudeDeg = Cesium.Math.toDegrees(cartographic.latitude)

      // WGS84 -> BD09
      const bd09Coord = w.transform(
        [longitudeDeg, latitudeDeg],
        w.WGS84,
        w.BD09,
      )

      const projectedCoord = webMercatorProjection.project(
        new Cesium.Cartographic(
          Cesium.Math.toRadians(bd09Coord[0]),
          Cesium.Math.toRadians(bd09Coord[1]),
        ),
      )

      return new Cesium.Cartesian2(projectedCoord.x, projectedCoord.y)
    }

    /**
     * 重写反投影方法：Web墨卡托 → BD09 → WGS84
     */
    this._projection.unproject = function (cartesian, result) {
      const cartographic = webMercatorProjection.unproject(cartesian)

      const longitudeDeg = Cesium.Math.toDegrees(cartographic.longitude)
      const latitudeDeg = Cesium.Math.toDegrees(cartographic.latitude)

      // BD09 -> WGS84
      const wgs84Coord = w.transform(
        [longitudeDeg, latitudeDeg],
        w.BD09,
        w.WGS84,
      )

      return new Cesium.Cartographic(
        Cesium.Math.toRadians(wgs84Coord[0]),
        Cesium.Math.toRadians(wgs84Coord[1]),
      )
    }
  }
}
